import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowAvatar,
  AdminRowMenuItem,
  AdminFixedRowActionsMenu,
  AdminRowActionsButton,
  AdminSupportingText,
  AdminListFilterDropdown,
  AdminRowMenuIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import useClickOutside from '../../../Hooks/useClickOutside';
import useFixedRowActionsMenu, { ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useFixedRowActionsMenu';
import defaultUserAvatar from '../../../assets/img/User.png';
import { getReviewsForScenarios } from '../../../data/reviewModerationScenarios';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';

// Filter options (Reviews Screen 1)
const REVIEW_STATUS_OPTIONS = ['Published', 'Under Review', 'Hidden', 'Removed', 'Deleted'];
const REVIEW_STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  ...REVIEW_STATUS_OPTIONS.map((status) => ({ value: status, label: status })),
];
const FLAG_STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  { value: 'With Open Flags', label: 'With Open Flags' },
  { value: 'No Open Flags', label: 'No Open Flags' },
];

/** List columns — date and time (matches posts / support queue lists). */
const formatListDateTime = (value) => {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const datePart = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart}, ${timePart}`;
};

const reviewPreviewFromContent = (content, maxLen = 60) => {
  const text = String(content || '').trim();
  if (!text) return '—';
  if (text.length <= maxLen) return text;
  return `${text.substring(0, maxLen).trimEnd()}…`;
};

const resolveReplyStatus = (review) => {
  if (review.replyStatus) return review.replyStatus;
  const reply = review.reply;
  if (!reply || !reply.hasReply) return 'Not Replied';
  if (reply.status === 'Deleted') return 'Deleted';
  return 'Replied';
};

// Review ID | Account | Rating | Review Preview | Flags | Review Status | Posted On | Last Updated | Action
const gridTemplateColumns =
  'minmax(96px, 1.05fr) minmax(120px, 1.4fr) minmax(64px, 0.7fr) minmax(160px, 2.2fr) minmax(72px, 0.7fr) minmax(100px, 1.1fr) minmax(118px, 1.25fr) minmax(118px, 1.25fr) minmax(64px, 0.6fr)';

const AllReviews = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [filters, setFilters] = useState({
    status: null,
    flag: null,
  });

  const [reviewStatusOpen, setReviewStatusOpen] = useState(false);
  const [flagStatusOpen, setFlagStatusOpen] = useState(false);

  const reviewStatusRef = useRef(null);
  const flagStatusRef = useRef(null);

  // Click outside handlers for filters
  useClickOutside(reviewStatusRef, () => setReviewStatusOpen(false));
  useClickOutside(flagStatusRef, () => setFlagStatusOpen(false));

  const [reviewRowActionsOpen, setReviewRowActionsOpen] = useState({});
  const reviewRowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);
  const {
    layout: reviewRowActionsMenuLayout,
    menuRef: reviewRowActionsMenuRef,
    placementReady: reviewRowActionsMenuPlacementReady,
  } = useFixedRowActionsMenu(
    reviewRowActionsOpen,
    reviewRowActionsRefs,
    tableScrollRef,
    ROW_ACTIONS_MENU_HEIGHT.SINGLE
  );

  const toggleReviewRowActions = (rowKey) => {
    setReviewRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(reviewRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setReviewRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Global handler to close all filter dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      const filterRefs = [
        reviewStatusRef, flagStatusRef
      ];
      
      const isOutsideAll = filterRefs.every(ref => {
        const el = ref?.current;
        return !el || !el.contains(event.target);
      });

      if (isOutsideAll) {
        setReviewStatusOpen(false);
        setFlagStatusOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 250)); // Simulate API call
      // Use scenario-driven reviews instead of generated dummy data
      const scenarios = getReviewsForScenarios();
      const reviewsData = scenarios.map((r) => {
        const postedAt = r.postedAt || r.submittedAt || new Date().toISOString();
        const previewSource = r.fullText || '';
        return {
          ...r,
          id: r.id,
          reviewId: r.reviewId,
          customer: {
            name: r.reviewer?.name || '—',
            id: r.reviewer?.id || null,
            avatar: r.reviewer?.avatar || null,
            status: r.reviewer?.status || 'Active',
          },
          business: {
            name: r.business?.name || '—',
            id: r.business?.id || null,
            logo: r.business?.logo || null,
            verified: r.business?.verified || false,
            category: r.business?.category || null,
            state: r.business?.state || null,
            suburb: r.business?.suburb || null,
          },
          rating: r.rating || 0,
          status: r.status || 'Published',
          reviewPreview: reviewPreviewFromContent(previewSource),
          fullText: previewSource,
          replyStatus: resolveReplyStatus(r),
          submittedAt: postedAt,
          postedAt,
          lastUpdated: r.lastUpdated || postedAt,
          interactionId: r.interactionId || r.jobQuote?.id || r.jobQuoteId || null,
          interactionType: r.interactionType || null,
          hasPhotos: (r.attachments || []).length > 0,
          images: r.attachments || [],
          openFlagsCount: typeof r.openFlagsCount === 'number' ? r.openFlagsCount : 0,
        };
      });
      setReviews(reviewsData);
      setLoading(false);
    };
    
    fetchReviews();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters]);

  // Handle filter change - Comprehensive handler for all filter types
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => {
      switch (filterType) {
        case 'status':
          return { ...prev, status: value };
        case 'flag':
          return { ...prev, flag: value };
        default:
          return prev;
      }
    });
  };

  const handleViewReview = (review) => {
    const reviewId = review.reviewId || review.id;
        navigate(`/admin/reviews/${reviewId}`, { state: { from: location.pathname, review } });
  };

  const filteredReviews = useMemo(() => {
    const normalizedQuery = debouncedSearch.trim().toLowerCase();

    const filtered = reviews.filter((review) => {
      // Search: Review ID, Customer Name, Business Name, Interaction ID (Review Title not used)
      const matchesSearch =
        !normalizedQuery ||
        (() => {
          if (review.reviewId?.toLowerCase().includes(normalizedQuery)) return true;
          if (review.business?.name?.toLowerCase().includes(normalizedQuery)) return true;
          if (review.customer?.name?.toLowerCase().includes(normalizedQuery)) return true;
          if (review.interactionId?.toLowerCase().includes(normalizedQuery)) return true;
          return false;
        })();

      const matchesStatus = !filters.status || review.status === filters.status;

      const openCount = review.openFlagsCount ?? 0;
      const matchesFlag =
        !filters.flag ||
        filters.flag === 'All' ||
        (filters.flag === 'No Open Flags' && openCount === 0) ||
        (filters.flag === 'With Open Flags' && openCount > 0);

      return matchesSearch && matchesStatus && matchesFlag;
    });

    // Default order: Posted On newest → oldest (edits do not reorder)
    return filtered.sort((a, b) => {
      const aTime = new Date(a.postedAt || a.submittedAt || 0).getTime();
      const bTime = new Date(b.postedAt || b.submittedAt || 0).getTime();
      return bTime - aTime;
    });
  }, [reviews, debouncedSearch, filters]);

  const totalFilteredCount = filteredReviews.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, Math.max(1, Math.ceil(filteredReviews.length / CONFIG_LIST_PAGE_SIZE))));
  }, [filteredReviews.length]);

  const startIndex = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
  const currentPageReviews = filteredReviews.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE);

  const rangeStart =
    totalFilteredCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalFilteredCount);
  const showPagination = totalFilteredCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Reviews</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Review ID, customer name, business name, interaction ID"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              aria-label="Search reviews"
            />

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Review Status"
                value={filters.status}
                options={REVIEW_STATUS_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('status', value)}
                menuRef={reviewStatusRef}
                open={reviewStatusOpen}
                setOpen={setReviewStatusOpen}
              />
              <AdminListFilterDropdown
                label="Open Flags"
                value={filters.flag}
                options={FLAG_STATUS_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('flag', value)}
                menuRef={flagStatusRef}
                open={flagStatusOpen}
                setOpen={setFlagStatusOpen}
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="h-12 bg-white animate-pulse rounded" />
              ))}
            </div>
          ) : filteredReviews.length === 0 ? (
            <div className="text-center py-10">
              <AdminSupportingText className="text-mediumgray">
                No reviews match your filters.
              </AdminSupportingText>
            </div>
          ) : (
            <>
              <div ref={tableScrollRef} className="overflow-x-auto">
                <div className="min-w-full">
                  <div
                    className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
                    style={{ gridTemplateColumns }}
                  >
                    <div className="flex items-center">
                      <AdminTableHeader>Review ID</AdminTableHeader>
                    </div>
                    <div className="flex items-center">
                      <AdminTableHeader>Account</AdminTableHeader>
                    </div>
                    <div className="flex items-center">
                      <AdminTableHeader>Rating</AdminTableHeader>
                    </div>
                    <div className="flex items-center pr-4">
                      <AdminTableHeader>Review Preview</AdminTableHeader>
                    </div>
                    <div className="flex items-center">
                      <AdminTableHeader>Flags</AdminTableHeader>
                    </div>
                    <div className="flex items-center">
                      <AdminTableHeader>Review Status</AdminTableHeader>
                    </div>
                    <div className="flex items-center">
                      <AdminTableHeader>Posted On</AdminTableHeader>
                    </div>
                    <div className="flex items-center">
                      <AdminTableHeader>Last Updated</AdminTableHeader>
                    </div>
                    <div className="flex items-center justify-center">
                      <AdminTableHeader>Action</AdminTableHeader>
                    </div>
                  </div>

                  {currentPageReviews.map((review) => {
                    const reviewId = review.reviewId || review.id;
                    const preview = review.reviewPreview || reviewPreviewFromContent(review.fullText);
                    return (
                      <div
                        key={reviewId}
                        className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 cursor-pointer transition-colors hover:bg-offwhite"
                        style={{ gridTemplateColumns }}
                        onClick={() => handleViewReview(review)}
                      >
                        <div className="flex items-center">
                          <AdminTableBody>{reviewId}</AdminTableBody>
                        </div>

                        <div className="flex items-center gap-2 min-w-0">
                          <AdminRowAvatar
                            src={review.customer?.avatar || defaultUserAvatar}
                            alt={review.customer?.name || review.customerName || ''}
                            onError={(e) => {
                              e.target.src = defaultUserAvatar;
                            }}
                          />
                          <AdminTableBody
                            emphasis
                            className="truncate"
                            title={review.customer?.name || review.customerName || '—'}
                          >
                            {review.customer?.name || review.customerName || '—'}
                          </AdminTableBody>
                        </div>

                        <div className="flex items-center gap-1.5 min-w-0">
                          {Number.isFinite(review.rating) && review.rating > 0 ? (
                            <AdminTableBody className="tabular-nums">
                              {Number.isInteger(review.rating)
                                ? review.rating
                                : Math.round(Number(review.rating))}
                              <span className="text-warmyellow text-sm leading-none">★</span>
                            </AdminTableBody>
                          ) : (
                            <AdminTableBody>—</AdminTableBody>
                          )}
                        </div>

                        <div className="flex items-center min-w-0 pr-4">
                          <AdminTableBody className="truncate" title={preview}>
                            {preview}
                          </AdminTableBody>
                        </div>

                        <div className="flex items-center">
                          <AdminTableBody className="tabular-nums">
                            {review.openFlagsCount ?? 0}
                          </AdminTableBody>
                        </div>

                        <div className="flex items-center">
                          {review.status ? (
                            <AdminStatusBadge status={review.status} label={review.status} />
                          ) : (
                            <AdminTableBody>—</AdminTableBody>
                          )}
                        </div>

                        <div className="flex items-center min-w-0">
                          <AdminTableMeta
                            className="truncate"
                            title={formatListDateTime(review.postedAt || review.submittedAt)}
                          >
                            {formatListDateTime(review.postedAt || review.submittedAt)}
                          </AdminTableMeta>
                        </div>

                        <div className="flex items-center min-w-0">
                          <AdminTableMeta
                            className="truncate"
                            title={formatListDateTime(review.lastUpdated)}
                          >
                            {formatListDateTime(review.lastUpdated)}
                          </AdminTableMeta>
                        </div>

                        <div
                          className="flex items-center justify-center relative"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            className="relative inline-flex"
                            ref={(el) => {
                              reviewRowActionsRefs.current[reviewId] = el;
                            }}
                          >
                            <AdminRowActionsButton
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleReviewRowActions(reviewId);
                              }}
                              title="Actions"
                              aria-label="Row actions"
                            >
                              <AdminRowMenuIcon />
                            </AdminRowActionsButton>
                            {reviewRowActionsOpen[reviewId] && reviewRowActionsMenuLayout && (
                              <AdminFixedRowActionsMenu
                                layout={reviewRowActionsMenuLayout}
                                menuRef={reviewRowActionsMenuRef}
                                placementReady={reviewRowActionsMenuPlacementReady}
                              >
                                <AdminRowMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewReview(review);
                                    setReviewRowActionsOpen({});
                                  }}
                                >
                                  View
                                </AdminRowMenuItem>
                              </AdminFixedRowActionsMenu>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={filteredReviews.length > 0 && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalFilteredCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default AllReviews;

